import { MapPin, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { EntityManageSection } from '@/components/ui/entity-manage-section'
import type { OrganisationLocation } from '@/hooks/use-organisations'
import {
  formatOrganisationLocationLine,
  formatOrganisationLocationPlace,
  organisationLocations,
} from '@/lib/organisation-locations'
import { profileSectionEmptyCtaClass } from '@/lib/profile-section-styles'

export type OrganisationLocationsSectionProps = {
  locations?: OrganisationLocation[] | null
  onEdit?: () => void
  emptyVariant?: 'hide' | 'cta'
  emptyCtaLabel?: string
  agentHighlight?: string
}

export function OrganisationLocationsSection({
  locations,
  onEdit,
  emptyVariant = 'cta',
  emptyCtaLabel = 'Add a location',
  agentHighlight,
}: OrganisationLocationsSectionProps) {
  const rows = organisationLocations({ locations })

  const list = (
    <ul className="space-y-3">
      {rows.map((location) => {
        const place = formatOrganisationLocationPlace(location)
        const line = formatOrganisationLocationLine(location)
        const description = location.description?.trim() ?? ''
        const showDescription = Boolean(description && place !== description)
        return (
          <li key={location.id} className="text-sm">
            <p className="font-medium text-foreground">{place || line}</p>
            {showDescription ? <p className="text-muted-foreground">{description}</p> : null}
          </li>
        )
      })}
    </ul>
  )

  if (rows.length === 0) {
    if (emptyVariant === 'hide' || !onEdit) return null
    return (
      <EntityManageSection
        title="Locations"
        leadingIcon={MapPin}
        subtitle="Where this organisation works"
        onEdit={onEdit}
        editAriaLabel="Add a location"
        editTitle="Add a location"
        agentHighlight={agentHighlight}
      >
        <Button type="button" variant="outline" className={profileSectionEmptyCtaClass} onClick={onEdit}>
          <Plus className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          {emptyCtaLabel}
        </Button>
      </EntityManageSection>
    )
  }

  if (!onEdit) {
    return (
      <section className="space-y-4">
        <h2 className="text-xl font-semibold">Locations</h2>
        {list}
      </section>
    )
  }

  return (
    <EntityManageSection
      title="Locations"
      leadingIcon={MapPin}
      subtitle="Where this organisation works"
      onEdit={onEdit}
      editAriaLabel="Edit locations"
      editTitle="Edit locations"
      agentHighlight={agentHighlight}
    >
      {list}
    </EntityManageSection>
  )
}
